import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";

/**
 * Shape of GET /api/me. Hand-written for Phase 0; replaced by the orval-generated
 * type once the backend's OpenAPI is available.
 */
export interface ClientScope {
  client_id: string;
  client_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
}

/** Table privilege level: nothing, records the user owns (created by / assigned to / about them), or every record. */
export type AccessLevel = "none" | "own" | "all";

/** Effective privileges on one table (highest level across the user's roles). */
export interface TableAccess {
  create: boolean;
  read: AccessLevel;
  write: AccessLevel;
  delete: AccessLevel;
}

export interface Me {
  id: string;
  display_name: string;
  email: string;
  is_staff: boolean;
  roles: string[];
  /** Additional + column-security codes, plus derived `<workspace>.access` codes. */
  permissions: string[];
  /** Readable tables only; a missing entity means no access. */
  tables: Record<string, TableAccess>;
  /** Menu items ("<workspace>/<section>") hidden because every one of the user's roles hides them. */
  hidden_nav?: string[];
  client_scopes: ClientScope[];
  /** Set while an administrator is viewing Pulse as another user or role. */
  view_as?: ViewAsInfo | null;
  /** Whether "View as" is available to this user (administrators, outside production). */
  can_view_as?: boolean;
}

export interface ViewAsInfo {
  kind: "user" | "role";
  target_id: string;
  /** "Jane Smith" or "Team Member role". */
  label: string;
  real_user_id: string;
  real_display_name: string;
}

export const meQueryKey = ["me"] as const;

export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => apiFetch<Me>("/me", { signal }),
    staleTime: 5 * 60_000,
  });
}
